/**
 * 📊 MonitoringPanel — Validation & Monitoring configuration for MASP issues
 *
 * Sections:
 * - Validation mode selector (time / volume / indicator / manual)
 * - Mode-specific fields
 * - Manual inspection log
 * - Monitoring configuration (manual / indicator)
 */

import React, { useState, useCallback } from 'react';
import {
  Clock, BarChart3, Activity, ClipboardCheck, Eye,
  Plus, Check, X, AlertTriangle, CalendarDays,
} from 'lucide-react';
import type {
  ValidationConfig, ValidationMode, MonitoringConfig, MonitoringMode,
  ManualInspection,
} from '../../types/issue';
import { VALIDATION_MODE_LABELS, MONITORING_MODE_LABELS } from '../../types/issue';

interface MonitoringPanelProps {
  validationConfig: ValidationConfig | null;
  monitoringConfig: MonitoringConfig | null;
  issueStatus: string;
  onSaveValidation: (config: ValidationConfig) => Promise<void>;
  onSaveMonitoring: (config: MonitoringConfig) => Promise<void>;
  onAddInspection: (inspection: { inspection_date: string; notes: string }) => Promise<void>;
  onCompleteValidation: (result: 'passed' | 'failed') => Promise<void>;
  readOnly?: boolean;
}

const MODE_ICONS: Record<ValidationMode, React.FC<{ size?: number; className?: string }>> = {
  time: Clock,
  volume: BarChart3,
  indicator: Activity,
  manual: ClipboardCheck,
};

export const MonitoringPanel: React.FC<MonitoringPanelProps> = ({
  validationConfig,
  monitoringConfig,
  issueStatus,
  onSaveValidation,
  onSaveMonitoring,
  onAddInspection,
  onCompleteValidation,
  readOnly = false,
}) => {
  const [activeTab, setActiveTab] = useState<'validation' | 'monitoring'>(
    issueStatus === 'monitoring' ? 'monitoring' : 'validation',
  );
  const [saving, setSaving] = useState(false);

  // Validation state
  const [valMode, setValMode] = useState<ValidationMode>(validationConfig?.mode ?? 'time');
  const [valDays, setValDays] = useState(validationConfig?.time?.validation_days ?? 30);
  const [valUnits, setValUnits] = useState(validationConfig?.volume?.validation_units ?? 100);
  const [valIndicatorId, setValIndicatorId] = useState(validationConfig?.indicator?.indicator_id ?? '');
  const [valTargetValue, setValTargetValue] = useState(validationConfig?.indicator?.target_value ?? 0);
  const [valPeriod, setValPeriod] = useState(validationConfig?.indicator?.validation_period ?? 30);

  // Manual inspection state
  const [inspDate, setInspDate] = useState('');
  const [inspNotes, setInspNotes] = useState('');
  const [showInspForm, setShowInspForm] = useState(false);

  // Monitoring state
  const [monMode, setMonMode] = useState<MonitoringMode>(monitoringConfig?.mode ?? 'manual');
  const [monIndicatorId, setMonIndicatorId] = useState(monitoringConfig?.indicator_id ?? '');
  const [monThreshold, setMonThreshold] = useState(monitoringConfig?.threshold_value ?? 0);

  const isValidationReadOnly = readOnly || issueStatus === 'monitoring';
  const isMonitoringReadOnly = readOnly || issueStatus === 'closed';

  const handleSaveValidation = useCallback(async () => {
    setSaving(true);
    try {
      const config: ValidationConfig = {
        mode: valMode,
        started_at: validationConfig?.started_at ?? new Date().toISOString(),
        inspections: validationConfig?.inspections ?? [],
        result: validationConfig?.result ?? null,
      };
      if (valMode === 'time') config.time = { validation_days: valDays };
      if (valMode === 'volume') config.volume = { validation_units: valUnits };
      if (valMode === 'indicator') config.indicator = {
        indicator_id: valIndicatorId,
        target_value: valTargetValue,
        validation_period: valPeriod,
      };
      await onSaveValidation(config);
    } finally {
      setSaving(false);
    }
  }, [valMode, valDays, valUnits, valIndicatorId, valTargetValue, valPeriod, validationConfig, onSaveValidation]);

  const handleAddInspection = useCallback(async () => {
    if (!inspDate || !inspNotes.trim()) return;
    setSaving(true);
    try {
      await onAddInspection({ inspection_date: inspDate, notes: inspNotes.trim() });
      setInspDate('');
      setInspNotes('');
      setShowInspForm(false);
    } finally {
      setSaving(false);
    }
  }, [inspDate, inspNotes, onAddInspection]);

  const handleSaveMonitoring = useCallback(async () => {
    setSaving(true);
    try {
      const config: MonitoringConfig = {
        mode: monMode,
        started_at: monitoringConfig?.started_at ?? new Date().toISOString(),
        checks_count: monitoringConfig?.checks_count ?? 0,
      };
      if (monMode === 'indicator') {
        config.indicator_id = monIndicatorId;
        config.threshold_value = monThreshold;
      }
      await onSaveMonitoring(config);
    } finally {
      setSaving(false);
    }
  }, [monMode, monIndicatorId, monThreshold, monitoringConfig, onSaveMonitoring]);

  const tabs = [
    { key: 'validation' as const, label: 'Validação', icon: Shield },
    { key: 'monitoring' as const, label: 'Monitoramento', icon: Eye },
  ];

  const inputClass = 'w-full rounded border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] text-slate-700 focus:border-blue-400 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300';

  return (
    <div className="space-y-3">
      {/* Tab bar */}
      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 dark:bg-slate-800">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-[10px] font-semibold transition ${
                activeTab === tab.key
                  ? 'bg-white text-slate-700 shadow-sm dark:bg-slate-700 dark:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
              }`}
            >
              <Icon size={11} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ═══ VALIDATION TAB ═══ */}
      {activeTab === 'validation' && (
        <div className="space-y-3">
          {/* Mode selector */}
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Modo de Validação
            </label>
            <div className="grid grid-cols-4 gap-1">
              {(Object.entries(VALIDATION_MODE_LABELS) as [ValidationMode, string][]).map(([mode, label]) => {
                const Icon = MODE_ICONS[mode];
                return (
                  <button
                    key={mode}
                    type="button"
                    disabled={isValidationReadOnly}
                    onClick={() => setValMode(mode)}
                    className={`flex flex-col items-center gap-0.5 rounded border px-1.5 py-2 text-[9px] font-medium transition ${
                      valMode === mode
                        ? 'border-blue-400 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/30 dark:text-blue-400'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300 dark:border-slate-700 dark:text-slate-400'
                    }`}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode-specific fields */}
          {valMode === 'time' && (
            <div>
              <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                Dias de Validação
              </label>
              <input
                type="number"
                min={1}
                value={valDays}
                onChange={e => setValDays(Number(e.target.value))}
                className={inputClass}
                disabled={isValidationReadOnly}
              />
              <p className="mt-0.5 text-[9px] text-slate-400">Problema será monitorado por este período sem recorrência</p>
            </div>
          )}

          {valMode === 'volume' && (
            <div>
              <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                Unidades para Validação
              </label>
              <input
                type="number"
                min={1}
                value={valUnits}
                onChange={e => setValUnits(Number(e.target.value))}
                className={inputClass}
                disabled={isValidationReadOnly}
              />
              <p className="mt-0.5 text-[9px] text-slate-400">Quantidade de unidades/lotes sem defeito para validar</p>
            </div>
          )}

          {valMode === 'indicator' && (
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                  ID do Indicador
                </label>
                <input
                  value={valIndicatorId}
                  onChange={e => setValIndicatorId(e.target.value)}
                  placeholder="UUID do indicador vinculado"
                  className={inputClass}
                  disabled={isValidationReadOnly}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                    Valor Alvo
                  </label>
                  <input
                    type="number"
                    value={valTargetValue}
                    onChange={e => setValTargetValue(Number(e.target.value))}
                    className={inputClass}
                    disabled={isValidationReadOnly}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                    Período (dias)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={valPeriod}
                    onChange={e => setValPeriod(Number(e.target.value))}
                    className={inputClass}
                    disabled={isValidationReadOnly}
                  />
                </div>
              </div>
            </div>
          )}

          {valMode === 'manual' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Inspeções Registradas
                </label>
                {!isValidationReadOnly && (
                  <button
                    type="button"
                    onClick={() => setShowInspForm(true)}
                    className="flex items-center gap-0.5 text-[10px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    <Plus size={10} />
                    Nova Inspeção
                  </button>
                )}
              </div>

              {/* Inspection list */}
              {(validationConfig?.inspections ?? []).length === 0 ? (
                <p className="text-[10px] text-slate-400 italic">Nenhuma inspeção registrada</p>
              ) : (
                <div className="max-h-[120px] space-y-1.5 overflow-y-auto">
                  {(validationConfig?.inspections ?? []).map((insp, idx) => (
                    <div key={idx} className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-800/60">
                      <div className="flex items-center gap-1.5 text-[10px]">
                        <CalendarDays size={10} className="text-slate-400" />
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {new Date(insp.inspection_date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <p className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400">{insp.notes}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* New inspection form */}
              {showInspForm && (
                <div className="space-y-1.5 rounded border border-blue-200 bg-blue-50/50 p-2 dark:border-blue-800/40 dark:bg-blue-900/10">
                  <input
                    type="date"
                    value={inspDate}
                    onChange={e => setInspDate(e.target.value)}
                    className={inputClass}
                  />
                  <textarea
                    value={inspNotes}
                    onChange={e => setInspNotes(e.target.value)}
                    placeholder="Observações da inspeção..."
                    className={`${inputClass} min-h-[50px] resize-y`}
                    rows={2}
                  />
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={handleAddInspection}
                      disabled={saving || !inspDate || !inspNotes.trim()}
                      className="flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-[10px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Check size={10} />
                      Registrar
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowInspForm(false)}
                      className="flex items-center gap-1 rounded border border-slate-200 px-2.5 py-1 text-[10px] text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400"
                    >
                      <X size={10} />
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Validation result */}
          {validationConfig?.result && (
            <div className={`flex items-center gap-1.5 rounded px-3 py-2 text-[11px] font-medium ${
              validationConfig.result === 'passed'
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
            }`}>
              {validationConfig.result === 'passed' ? <Check size={12} /> : <AlertTriangle size={12} />}
              {validationConfig.result === 'passed' ? 'Validação Aprovada' : 'Validação Reprovada'}
            </div>
          )}

          {/* Action buttons */}
          {!isValidationReadOnly && (
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={handleSaveValidation}
                disabled={saving}
                className="flex-1 rounded bg-blue-600 py-1.5 text-[10px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar Configuração'}
              </button>
              {issueStatus === 'validation' && !validationConfig?.result && (
                <>
                  <button
                    type="button"
                    onClick={() => onCompleteValidation('passed')}
                    className="rounded bg-emerald-600 px-3 py-1.5 text-[10px] font-semibold text-white hover:bg-emerald-700"
                  >
                    Aprovar
                  </button>
                  <button
                    type="button"
                    onClick={() => onCompleteValidation('failed')}
                    className="rounded bg-red-600 px-3 py-1.5 text-[10px] font-semibold text-white hover:bg-red-700"
                  >
                    Reprovar
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ MONITORING TAB ═══ */}
      {activeTab === 'monitoring' && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Modo de Monitoramento
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {(Object.entries(MONITORING_MODE_LABELS) as [MonitoringMode, string][]).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  disabled={isMonitoringReadOnly}
                  onClick={() => setMonMode(mode)}
                  className={`flex items-center justify-center gap-1.5 rounded border px-3 py-2 text-[10px] font-medium transition ${
                    monMode === mode
                      ? 'border-cyan-400 bg-cyan-50 text-cyan-700 dark:border-cyan-500 dark:bg-cyan-900/30 dark:text-cyan-400'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300 dark:border-slate-700 dark:text-slate-400'
                  }`}
                >
                  {mode === 'manual' ? <ClipboardCheck size={12} /> : <Activity size={12} />}
                  {label}
                </button>
              ))}
            </div>
          </div>

          {monMode === 'indicator' && (
            <div className="space-y-2">
              <div>
                <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                  ID do Indicador
                </label>
                <input
                  value={monIndicatorId}
                  onChange={e => setMonIndicatorId(e.target.value)}
                  placeholder="UUID do indicador de monitoramento"
                  className={inputClass}
                  disabled={isMonitoringReadOnly}
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                  Valor Limite (Threshold)
                </label>
                <input
                  type="number"
                  value={monThreshold}
                  onChange={e => setMonThreshold(Number(e.target.value))}
                  className={inputClass}
                  disabled={isMonitoringReadOnly}
                />
                <p className="mt-0.5 text-[9px] text-slate-400">Se o indicador ultrapassar este valor, o problema será reaberto automaticamente</p>
              </div>
            </div>
          )}

          {/* Monitoring status */}
          {monitoringConfig?.started_at && (
            <div className="space-y-1.5 rounded border border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-slate-700 dark:bg-slate-800/60">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500 dark:text-slate-400">Início</span>
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  {new Date(monitoringConfig.started_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {monitoringConfig.last_check_at && (
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-slate-500 dark:text-slate-400">Última Verificação</span>
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {new Date(monitoringConfig.last_check_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500 dark:text-slate-400">Verificações</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {monitoringConfig.checks_count ?? 0}
                </span>
              </div>
            </div>
          )}

          {/* Save button */}
          {!isMonitoringReadOnly && (
            <button
              type="button"
              onClick={handleSaveMonitoring}
              disabled={saving}
              className="w-full rounded bg-cyan-600 py-1.5 text-[10px] font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Monitoramento'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Shield icon imported inline for tab — re-export
const Shield: React.FC<{ size?: number; className?: string }> = ({ size = 16, className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
);

export default MonitoringPanel;
